export const enum BookingMode {
  BOOKING = 1,
  BLOCKED = 2,
}

export enum BookingStatus {
  New = 0,
  Pending = 1,
  Request = 2,
  Completed = 3,
  Cancel = 4,
  Rejected = 5,
}
export enum SearchMode {
  BookingPeriod = 1,
  BookingCreatedOn = 2,
}

export enum NotificationType {
  Booking_Confirmation = 1100,
  Booking_PaymentPending = 1110,
  Booking_Cancellation = 1200,
  Booking_RenewFixedBooking = 5505,
}

export enum ImportType {
  Booking = 1,
  Schedule = 2,
}

// Mirrors Abook.DomainModel/Enums/AdditionalServiceCalculationType.cs
export enum AdditionalServiceCalculationType {
  Default = 1,
  PerNight = 2,
}

export interface ResourceEntryLike {
  id?: number;
  serviceId?: number;
  name?: string;
  price?: number;
  minAmount?: number;
  maxAmount?: number;
  calculationType?: AdditionalServiceCalculationType;
}

export interface BookedEntryDisplaySeed {
  resourceEntry: ResourceEntryLike | undefined;
  wasTransferred: boolean;
  seedAmount: number | undefined;
  seedPrice: number | undefined;
}

/**
 * A booked addon's config row (e.g. "breakfast") belongs to one facility. When a period's
 * facility is changed within the dialog before saving, the API's own remap
 * (BookingPeriodService.RemapAdditionalServicesToResourceAsync) will move the row on save — but
 * until then the dialog would otherwise keep showing the old facility's price/amount for an
 * already-booked entry. This looks up the new facility's counterpart by the shared serviceId (not
 * the row id, which differs per facility) so the dialog can display what the row will become,
 * without writing anything itself.
 */
export function seedBookedEntryForDisplay(
  entry: {
    resourceEntry?: ResourceEntryLike;
    resourceEntryId?: number;
    amount?: number;
    dailyAmount?: number;
    price?: number;
  },
  resourceEntriesByServiceId: Map<number | undefined, ResourceEntryLike>,
): BookedEntryDisplaySeed {
  const transferredResourceEntry = entry.resourceEntry
    ? resourceEntriesByServiceId.get(entry.resourceEntry.serviceId)
    : undefined;
  const wasTransferred = transferredResourceEntry != null && transferredResourceEntry.id !== entry.resourceEntryId;
  const resourceEntry = transferredResourceEntry ?? entry.resourceEntry;

  const newCalculationType = resourceEntry?.calculationType;
  const oldCalculationType = entry.resourceEntry?.calculationType;
  const minAmount = resourceEntry?.minAmount ?? 0;
  // A transferred entry's old amount was recorded under the old facility's calculationType (a
  // flat count vs. a per-night rate mean different things) - if the new facility's row uses a
  // different one, that number no longer means the same thing, so fall back to the new
  // facility's minimum instead of silently mis-displaying a wrong quantity.
  const calculationTypeChanged = wasTransferred && newCalculationType !== oldCalculationType;
  const seedAmount = calculationTypeChanged
    ? minAmount
    : isPerNightService(newCalculationType)
      ? (entry.dailyAmount ?? entry.amount)
      : entry.amount;
  const seedPrice = wasTransferred ? (resourceEntry?.price ?? entry.price) : entry.price;

  return { resourceEntry, wasTransferred, seedAmount, seedPrice };
}

/** Missing/undefined calculationType is Default — matches unchanged historical behavior. */
export function isPerNightService(calculationType?: AdditionalServiceCalculationType): boolean {
  return calculationType === AdditionalServiceCalculationType.PerNight;
}

/** Single source of truth for the amount×price×nights formula used by calcPrices(). */
export function computeAddonLineTotal(
  amount: number,
  unitPrice: number,
  calculationType: AdditionalServiceCalculationType | null | undefined,
  nights: number,
): number {
  const multiplier = isPerNightService(calculationType) ? nights : 1;
  return amount * unitPrice * multiplier;
}

/** The period fields whose change moves money. A note or an activity does not (#459). */
export const BILLABLE_PERIOD_FIELDS = [
  'resourceId',
  'start_date',
  'start_time',
  'end_date',
  'end_time',
  'price',
] as const;

/** Whether a service row was actually ordered. The save sends nothing for the rest. */
export function isOrderedAmount(amount?: number | string | null): boolean {
  return Number(amount ?? 0) !== 0;
}

/** Whether a service row changes what the booking is charged: edited, or about to be created. */
export function isBillableServiceChange(
  dirty: boolean,
  id?: number | null,
  amount?: number | string | null,
): boolean {
  return dirty || (!id && isOrderedAmount(amount));
}
